import { z } from 'zod'

/**
 * Teléfono peruano: 9 dígitos, con `+51` opcional al inicio.
 *
 * Cubre celulares (9 dígitos, empiezan en 9) y fijos con código de área (01 en Lima + 7
 * u 8 dígitos, que también dan 9). Vacío permitido, porque el campo es opcional en la
 * mayoría de los formularios; donde es obligatorio, el schema que lo usa encadena su
 * propio `.min(1)`.
 *
 * Reemplaza al `phoneValidator` anterior, que permitía de 6 a 15 dígitos y no lo usaba
 * nadie: los formularios validaban por su cuenta o no validaban. De ahí que el directorio
 * aceptara teléfonos de 20 dígitos (observación 5, sep 2026).
 *
 * NOTA: el `maxLength` de los inputs sigue saliendo de `fieldLengths.ts`, que espeja las
 * columnas de la base. Ese número no es la regla de negocio y no hay que tocarlo; la
 * regla vive acá.
 */
export const PERU_PHONE_REGEX = /^(\+51)?\d{9}$/

/** DNI: 8 dígitos exactos. Expuesta para los formularios que traducen sus propios mensajes. */
export const DNI_REGEX = /^\d{8}$/

export const peruPhone = () =>
  z
    .string()
    .regex(PERU_PHONE_REGEX, 'Ingresa un celular de 9 dígitos')
    .optional()
    .or(z.literal(''))

/**
 * Número de cuenta bancaria.
 *
 * Los bancos peruanos escriben la cuenta con dígitos y guiones ("191-0123456789-01" en
 * el BCP) y el CCI son 20 dígitos corridos. No hay un formato único, así que se acepta
 * cualquier combinación de dígitos, guiones y espacios exigiendo al menos 8 dígitos
 * reales: alcanza para rechazar texto libre, que era lo reportado — el campo aceptaba
 * cualquier caracter (observación 20, sep 2026) — sin inventar una regla por banco que
 * terminaría rechazando cuentas legítimas.
 */
export const BANK_ACCOUNT_REGEX = /^[\d\s-]+$/

export const bankAccountNumber = () =>
  z
    .string()
    .min(1, 'shared.taxInfo.zodAccountNumberRequired')
    .max(30)
    .regex(BANK_ACCOUNT_REGEX, 'El número de cuenta solo admite dígitos y guiones')
    .refine(
      (valor) => (valor.match(/\d/g) ?? []).length >= 8,
      'El número de cuenta debe tener al menos 8 dígitos',
    )

export type DocumentKind = 'dni' | 'ce' | 'passport'

/**
 * Documento de identidad según su tipo (`PersonDocumentType` en el backend).
 *
 * El pasaporte no tiene un formato único internacional, así que sólo se comprueba que
 * sea alfanumérico de largo razonable: rechazar documentos extranjeros legítimos por
 * inventar una regla sería peor que no validar.
 */
export const documentValidator = (kind: DocumentKind) => {
  switch (kind) {
    case 'dni':
      return z.string().regex(DNI_REGEX, 'El DNI debe tener 8 dígitos')
    case 'ce':
      return z.string().regex(/^[A-Za-z0-9]{9,12}$/, 'El carné de extranjería debe tener entre 9 y 12 caracteres')
    case 'passport':
      return z.string().regex(/^[A-Za-z0-9]{6,15}$/, 'El pasaporte debe tener entre 6 y 15 caracteres alfanuméricos')
  }
}

/** Email opcional con formato válido. Cadena vacía permitida. */
export const optionalEmail = (max = 200) =>
  z
    .string()
    .max(max, `Máximo ${max} caracteres`)
    .email('Email inválido')
    .optional()
    .or(z.literal(''))

export const schemas = {
  ruc: z.string().length(11, 'RUC debe tener 11 digitos'),
  dni: documentValidator('dni'),
  email: z.string().email('Email invalido'),
  phone: peruPhone,
  required: (name: string) => z.string().min(1, `${name} es requerido`),
  optional: z.string().optional(),
}
